import {useAppDispatch, useAppSelector} from '../../stores/configureStore'
import css from '../../css/imgTools.module.css'
import {faSearch, faTrash, faUndo} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  addImageHistoryItem,
  deleteImage,
  persistRotation,
  persistScale,
  selectImage,
  setInert,
  setInteractedImage,
} from '../../stores/editor/editor.reducer'
import {Img} from '../../models/editor/img'
import {selectToolboxTabPanel} from '../../stores/toolbox/toolbox.reducer'
import {Interaction} from '../../stores/editor/editor.store'
import {IMG_REMOVED} from '../../models/editor/imgHistoryItemAction'
import {useCallback, useEffect, useRef} from 'react'
import StyleSheet from '../../utils/helpers/styleSheet'

interface ImgToolsProps {
  position: StyleSheet
  img: Img
}

export default function ImgTools({position, img}: ImgToolsProps) {
  const dispatch = useAppDispatch()
  const {interactedImage} = useAppSelector(store => store.editor)

  const persistImageOrigValueByInteraction = useCallback(
    (interaction: Interaction) => {
      if ('scaling' === interaction) {
        dispatch(persistScale({id: img.id}))
      }

      if ('rotating' === interaction) {
        dispatch(persistRotation({id: img.id}))
      }
    },
    [dispatch, img.id],
  )

  const onMouseDown = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    interaction: Interaction,
  ) => {
    e.preventDefault()
    persistImageOrigValueByInteraction(interaction)
    dispatch(
      setInteractedImage({
        interaction: interaction,
        id: img.id,
        type: img.type,
      }),
    )
    dispatch(setInert({inert: true, exceptId: img.id}))
    dispatch(selectImage({id: img.id}))
    dispatch(selectToolboxTabPanel({tabPanel: 'Edit'}))
  }

  const makeOnTouchStart = useCallback(
    (interaction: Interaction) =>
      function (this: HTMLDivElement, e: TouchEvent) {
        e.preventDefault()
        persistImageOrigValueByInteraction(interaction)
        dispatch(
          setInteractedImage({
            interaction: interaction,
            id: img.id,
            type: img.type,
          }),
        )
        dispatch(setInert({inert: true, exceptId: img.id}))
        dispatch(selectImage({id: img.id}))
        dispatch(selectToolboxTabPanel({tabPanel: 'Edit'}))
      },
    [dispatch, img.id, persistImageOrigValueByInteraction],
  )

  const scalingRef = useRef<HTMLDivElement>(null)
  const rotatingRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scalingRef.current && rotatingRef.current) {
      const scalingRefCurrent = scalingRef.current
      const rotatingRefCurrent = rotatingRef.current

      const onScalingTouchStart = makeOnTouchStart('scaling')
      const onRotatingTouchStart = makeOnTouchStart('rotating')

      rotatingRefCurrent.addEventListener('touchstart', onRotatingTouchStart, {
        passive: false,
      })
      scalingRefCurrent.addEventListener('touchstart', onScalingTouchStart, {
        passive: false,
      })
      return () => {
        rotatingRefCurrent.removeEventListener(
          'touchstart',
          onRotatingTouchStart,
        )
        scalingRefCurrent.removeEventListener('touchstart', onScalingTouchStart)
      }
    }
  }, [makeOnTouchStart])

  return (
    <>
      <div
        className={`${css.imgTools} print-hidden`}
        style={{
          ...position,
          pointerEvents: interactedImage ? 'none' : 'auto',
        }}>
        <div
          ref={scalingRef}
          className={`${css.imgButton} ${css.imgButtonUpDown}`}
          onMouseDown={e => onMouseDown(e, 'scaling')}>
          <FontAwesomeIcon icon={faSearch} />
        </div>

        <div
          ref={rotatingRef}
          className={`${css.imgButton} ${css.imgButtonUpDown}`}
          onMouseDown={e => onMouseDown(e, 'rotating')}>
          <FontAwesomeIcon icon={faUndo} />
        </div>
        <div
          className={css.imgButton}
          onClick={() => {
            dispatch(addImageHistoryItem({item: {img, action: IMG_REMOVED}}))
            dispatch(deleteImage({id: img.id}))
            dispatch(selectToolboxTabPanel({tabPanel: null}))
          }}
          onTouchStart={() => {
            dispatch(addImageHistoryItem({item: {img, action: IMG_REMOVED}}))
            dispatch(deleteImage({id: img.id}))
            dispatch(selectToolboxTabPanel({tabPanel: null}))
          }}>
          <FontAwesomeIcon icon={faTrash} />
        </div>
      </div>
    </>
  )
}
